import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { LineDetails, Plan } from './types/api';
import { onlyDigits } from './utils/phone';

/**
 * Sessão da linha validada.
 *
 * Substitui a sessão de arquivo do Laravel (`validation_*`, `planos_disponiveis`,
 * `dados_cliente_linha`, `validated_brand`). Fica em `sessionStorage`, que é
 * por aba e morre ao fechar o navegador — a semântica mais próxima da anterior.
 *
 * **Isto não é um controle de acesso.** Quem autoriza a recarga é a API, pelo
 * `linhaToken` emitido na validação do OTP. O que está aqui serve para montar
 * a tela; adulterar o `sessionStorage` não libera recarga nenhuma.
 *
 * **Vínculo com a marca.** A sessão não guarda mais a marca em que o OTP foi
 * validado (o antigo `brandSlug` e o "Security Alert: Brand Mismatch" do
 * middleware Laravel). Com um domínio por marca a garantia deixou de ser
 * código e passou a ser do navegador: o `sessionStorage` é particionado por
 * ORIGIN, então uma sessão gravada em `recarga.marcaA.com.br` é fisicamente
 * inacessível a partir de `recarga.marcaB.com.br`. Do lado do servidor, o
 * `linhaToken` continua sendo recusado quando é de outro tenant. A checagem só
 * voltaria a fazer falta se um mesmo hostname servisse duas marcas — que é
 * exatamente o que este modelo elimina.
 */

const STORAGE_KEY = 'recarga-facil:sessao';

export interface LineSession {
  /** Número validado, somente dígitos. */
  linha: string;
  /** Payload de detalhes devolvido pela API para a linha. */
  detalhes: LineDetails;
  /** Planos disponíveis, já filtrados. */
  planos: Plan[];
  /** Prova de validação por OTP, enviada junto na solicitação de recarga. */
  linhaToken: string | null;
  /**
   * Handle da sessão de OTP. Continua valendo depois da validação porque a
   * tokenização do cartão o usa para a API resolver a linha e o documento no
   * servidor — nunca a partir de campo preenchido pelo cliente.
   */
  otpSessionToken: string | null;
}

interface SessionContextValue {
  session: LineSession | null;
  /** Grava a sessão após o OTP ser validado com sucesso. */
  startSession: (session: LineSession) => void;
  clearSession: () => void;
  /** Nome do titular, com o mesmo fallback da tela antiga. */
  titular: string | null;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession precisa estar dentro de <SessionProvider>');
  }
  return context;
}

function readStoredSession(): LineSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LineSession;
    if (!parsed || typeof parsed.linha !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredSession(session: LineSession | null): void {
  try {
    if (session) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // sessionStorage indisponível (aba anônima com storage bloqueado):
    // a sessão passa a viver só em memória, o que é aceitável.
  }
}

interface SessionProviderProps {
  children: React.ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [session, setSession] = useState<LineSession | null>(() => readStoredSession());

  const startSession = useCallback((next: LineSession) => {
    const normalized: LineSession = { ...next, linha: onlyDigits(next.linha) };
    writeStoredSession(normalized);
    setSession(normalized);
  }, []);

  const clearSession = useCallback(() => {
    writeStoredSession(null);
    setSession(null);
  }, []);

  const titular = useMemo(() => {
    if (!session) return null;
    const detalhes = session.detalhes as Record<string, unknown>;
    const nome = detalhes?.titular ?? detalhes?.cliente_nome;
    return typeof nome === 'string' && nome.trim() ? nome : null;
  }, [session]);

  const value = useMemo<SessionContextValue>(
    () => ({ session, startSession, clearSession, titular }),
    [session, startSession, clearSession, titular]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
